import React, { useState, useRef, useEffect } from 'react';
import { FaRobot, FaPaperPlane, FaComment, FaTimes } from 'react-icons/fa';
import './BasicChat.css';

// Simple patterns and responses that don't need any external dependencies
const chatPatterns = [
  { 
    match: (msg) => /hello|hi|hey|greetings/i.test(msg), 
    response: "Hello! I'm your Student Helper Assistant. How can I help you today?"
  },
  { 
    match: (msg) => /help|assist|support/i.test(msg), 
    response: "I can help you with academic resources, career planning, campus info, achievements, Q&A, materials, profiles, and messaging. What do you need help with?"
  },
  { 
    match: (msg) => /achievement|point|rank|leaderboard|badge|trophy/i.test(msg), 
    response: "In the Achievements section, you can track your contributions, points, badges, and ranking on the leaderboard. Visit the Achievements page in the navigation menu to see your status."
  },
  { 
    match: (msg) => /question|answer|qa|q&a|forum/i.test(msg), 
    response: "Our Q&A section lets you ask questions and get answers from other students. You earn points by providing helpful answers."
  },
  { 
    match: (msg) => /material|resource|document|upload|download|study|note|book/i.test(msg), 
    response: "The Materials section allows you to share and download study resources. Materials are categorized by subject for easy searching."
  },
  { 
    match: (msg) => /profile|account|password|setting|preference/i.test(msg), 
    response: "You can manage your profile by clicking your username in the navigation menu. Update your info, change your password, and view your activity there."
  },
  { 
    match: (msg) => /message|chat|communication|inbox|conversation/i.test(msg), 
    response: "You can communicate with other users through our messaging system. Access your messages from the navigation menu."
  },
  { 
    match: (msg) => /exam|test|quiz|preparation|study/i.test(msg), 
    response: "Our Exam Preparation section offers study plans, practice tests, and revision materials to help you prepare for exams."
  },
  { 
    match: (msg) => /career|job|internship|resume|cv|interview/i.test(msg), 
    response: "The Career section provides job listings, internship opportunities, resume building tools, and interview preparation resources."
  },
  { 
    match: (msg) => /expert|tutor|teacher|instructor|mentor/i.test(msg), 
    response: "Connect with subject matter experts in our Expert Support section. You can schedule sessions or ask for specific guidance on difficult topics."
  },
  { 
    match: (msg) => /login|signin|register|signup|account|password/i.test(msg), 
    response: "You can login or create a new account from the navigation menu. If you've forgotten your password, there's a password recovery option on the login page."
  }
];

// Get chat response from patterns
const getChatResponse = (message) => {
  // Find a matching pattern
  for (const pattern of chatPatterns) {
    if (pattern.match(message)) {
      return pattern.response;
    }
  }
  
  // Default response
  return "I can help you navigate our Student Helper website. We offer features like academic resources, career planning, campus information, technology support, achievements tracking, Q&A forum, study materials, profile management, and messaging. What would you like to know more about?";
};

const BasicChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, text: 'Hi there! I\'m your Student Helper Assistant. How can I help you today?', isBot: true }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const handleSendMessage = async () => {
    if (inputValue.trim() === '') return;
    
    // User message
    const userMessage = {
      id: messages.length + 1,
      text: inputValue,
      isBot: false,
      timestamp: new Date().toISOString()
    };
    
    // Add user message to chat
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputValue('');
    setIsTyping(true);
    
    // Simulate response delay
    setTimeout(() => {
      // Get response
      const botResponse = getChatResponse(inputValue);
      
      // Bot message
      const botMessage = {
        id: messages.length + 2,
        text: botResponse,
        isBot: true,
        timestamp: new Date().toISOString()
      };
      
      // Add bot message to chat
      setMessages(prevMessages => [...prevMessages, botMessage]);
      setIsTyping(false);
    }, 500);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };
  
  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {!isOpen && (
        <div 
          className="basic-chat-bubble"
          onClick={() => setIsOpen(true)}
        >
          <FaComment />
          <span className="basic-chat-label">Chat with AI</span>
        </div>
      )}
      
      {isOpen && (
        <div className="basic-chat-container">
          <div className="basic-chat-header">
            <div className="basic-chat-title">
              <FaRobot className="basic-bot-icon" />
              <span>Student Helper Assistant</span>
            </div>
            
            <button 
              className="basic-close-chat" 
              onClick={() => setIsOpen(false)}
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="basic-chat-messages-container">
            {messages.map(message => (
              <div
                key={message.id}
                className={`basic-message ${message.isBot ? 'basic-bot' : 'basic-user'}`}
              >
                {message.isBot && <FaRobot className="basic-message-icon" />}
                <div className="basic-message-content">
                  <div className="basic-message-text">
                    {message.text}
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="basic-message basic-bot basic-typing">
                <FaRobot className="basic-message-icon" />
                <div className="basic-typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            
            {/* Reference for auto-scrolling */}
            <div ref={messagesEndRef} style={{ height: '1px', width: '100%' }} />
          </div>
          
          <div className="basic-chat-input">
            <input
              type="text"
              placeholder="Ask me anything..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button 
              className="basic-send-button" 
              onClick={handleSendMessage}
              disabled={inputValue.trim() === ''}
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BasicChat; 